import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import './Request.css';

const Request: React.FC = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [business, setBusiness] = useState('');
  const [notes, setNotes] = useState('');
  const [address, setAddress] = useState('');
  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');
  const [placeId, setPlaceId] = useState('');
  const [autocompleteUnavailableReason, setAutocompleteUnavailableReason] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const businessInputRef = useRef<HTMLInputElement | null>(null);

  const loadGoogleMapsPlaces = (): Promise<void> => {
    return new Promise((resolve, reject) => {
      const existing = (window as any).google?.maps?.places;
      if (existing) {
        resolve();
        return;
      }
      const rawApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';
      const apiKey = rawApiKey.trim().replace(/^"(.*)"$/, '$1').replace(/^'(.*)'$/, '$1');
      if (!apiKey) {
        setAutocompleteUnavailableReason('Business autocomplete is unavailable: missing Google Maps API key.');
        resolve();
        return;
      }
      const scriptId = 'google-maps-places';
      const expectedSrc = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&libraries=places&v=weekly`;
      const attachScript = (): void => {
        const script = document.createElement('script');
        script.id = scriptId;
        script.async = true;
        script.defer = true;
        script.src = expectedSrc;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error('Failed to load Google Maps script'));
        document.head.appendChild(script);
      };

      const prior = document.getElementById(scriptId) as HTMLScriptElement | null;
      if (prior) {
        if ((window as any).google?.maps?.places) {
          resolve();
          return;
        }

        // Always replace stale script tags if Places isn't ready yet.
        // This recovers from previous failed/blocked loads that won't emit new events.
        prior.remove();
        attachScript();
        return;
      }

      attachScript();
    });
  };

  useEffect(() => {
    let autocomplete: any;
    loadGoogleMapsPlaces()
      .then(() => {
        const google = (window as any).google;
        if (!google?.maps?.places || !businessInputRef.current) {
          setAutocompleteUnavailableReason(
            'Business autocomplete unavailable. Verify Maps JavaScript API + Places API are enabled and localhost is allowed in API key referrers.'
          );
          return;
        }
        const placesScript = document.getElementById('google-maps-places') as HTMLScriptElement | null;
        if (placesScript) placesScript.dataset.loaded = 'true';
        setAutocompleteUnavailableReason(null);
        autocomplete = new google.maps.places.Autocomplete(businessInputRef.current, {
          fields: ['name', 'formatted_address', 'geometry', 'place_id', 'address_components'],
          types: ['establishment'],
        });
        autocomplete.addListener('place_changed', () => {
          const place = autocomplete.getPlace();
          const placeName = place?.name || '';
          const formattedAddress = place?.formatted_address || '';
          const components = place?.address_components || [];
          const streetNumber = components.find((c: any) => c.types.includes('street_number'))?.long_name;
          const route = components.find((c: any) => c.types.includes('route'))?.long_name;
          const computedStreet = streetNumber || route ? `${streetNumber ? streetNumber + ' ' : ''}${route || ''}`.trim() : '';
          const lat = place?.geometry?.location?.lat ? String(place.geometry.location.lat()) : '';
          const lng = place?.geometry?.location?.lng ? String(place.geometry.location.lng()) : '';
          if (placeName) setBusiness(placeName);
          if (computedStreet) {
            setAddress(computedStreet);
          } else if (formattedAddress) {
            setAddress(formattedAddress);
          }
          if (lat) setLatitude(lat);
          if (lng) setLongitude(lng);
          if (place?.place_id) setPlaceId(place.place_id);
        });
      })
      .catch(() => {
        setAutocompleteUnavailableReason(
          'Business autocomplete failed to load. Check API key restrictions and ensure billing is enabled in Google Cloud.'
        );
      });

    return () => {
      // No explicit cleanup API for Autocomplete events needed in this simple usage
    };
  }, []);

  const encode = (data: Record<string, string>) =>
    Object.keys(data)
      .map((key) => encodeURIComponent(key) + '=' + encodeURIComponent(data[key]))
      .join('&');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitError(null);

    try {
      const response = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: encode({
          'form-name': 'request-camera',
          'bot-field': '',
          name,
          email,
          phone,
          business,
          address,
          latitude,
          longitude,
          place_id: placeId,
          notes,
        }),
      });

      if (!response.ok) {
        throw new Error(`Netlify form submission failed with status ${response.status}`);
      }

      setSubmitted(true);
    } catch (err) {
      setSubmitError('Submission failed. Please try again in a moment.');
    }
  };

  return (
    <div className="request-container">
      <div className="request-card">
        <Link to="/" className="exit-button" aria-label="Exit request form and return home">✕</Link>
        <h2>Request a Camera</h2>
        {!submitted ? (
          <form
            name="request-camera"
            method="POST"
            action="/"
            data-netlify="true"
            data-netlify-honeypot="bot-field"
            onSubmit={handleSubmit}
            className="request-form"
          >
            <input type="hidden" name="form-name" value="request-camera" />
            <input type="hidden" name="bot-field" />
            <input type="hidden" name="address" value={address} />
            <input type="hidden" name="latitude" value={latitude} />
            <input type="hidden" name="longitude" value={longitude} />
            <input type="hidden" name="place_id" value={placeId} />
            <label>
              <span>Name</span>
              <input name="name" value={name} onChange={(e) => setName(e.target.value)} required placeholder="Jane Doe" />
            </label>
            <label>
              <span>Email</span>
              <input name="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="jane@store.com" />
            </label>
            <label>
              <span>Phone Number</span>
              <input name="phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} required placeholder="(415) 555-1234" />
            </label>
            <label className="business-field">
              <span>Business name</span>
              <input
                ref={businessInputRef}
                name="business"
                value={business}
                onChange={(e) => setBusiness(e.target.value)}
                required
                placeholder="Market Street Store"
                autoComplete="off"
              />
              {autocompleteUnavailableReason && (
                <small className="autocomplete-note">{autocompleteUnavailableReason}</small>
              )}
            </label>
            <label>
              <span>Notes (optional)</span>
              <textarea name="notes" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Any details we should know?" />
            </label>
            {submitError && <small className="request-submit-error">{submitError}</small>}
            <button type="submit" className="request-btn">Submit</button>
          </form>
        ) : (
          <div className="request-success">
            <p>Thanks! We'll reach out shortly.</p>
            <Link to="/" className="home-link">Back to Home</Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default Request;


